'use client';

import { useRef, type ReactNode, type RefObject } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { NOT_REDUCED, playOnEnter } from '@/components/sections/kit/motion';
import type { Box, ConnectorSpec } from './iso';
import { IsoLabel, Shadow } from './parts';

gsap.registerPlugin(useGSAP, MotionPathPlugin, DrawSVGPlugin);

export type IsoObjectSpec = {
  id: string;
  /** Projected part markup (a part component already bound to its Box). */
  element: ReactNode;
  label?: { x: number; y: number; text: string; lead?: { y1: number; y2: number } };
};
export type IsoConnector = ConnectorSpec & { pulse?: boolean };
/** Per-object parallax depth + entrance rise (negative = drops in from above). */
export type IsoMotion = Record<string, { depth: number; rise: number }>;

type IsoSceneStageProps = {
  viewBox: string;
  /** Painter's order, back to front. */
  objects: IsoObjectSpec[];
  /** The hero object that lands first (its entrance beat); must be an object id. */
  coreId: string;
  connectors: IsoConnector[];
  motion: IsoMotion;
  shadows: Array<{ box: Box; rx?: number }>;
  /** Connector + node styling (security scene overrides to pink seals). */
  connectorStroke?: string;
  nodeFill?: string;
  nodeStroke?: string;
  /** Pointer-parallax surface; falls back to the svg. (The entrance trigger is always the svg.) */
  surfaceRef?: RefObject<HTMLElement | null>;
  className?: string;
};

/**
 * The shared isometric stage: renders a scene config server-side fully
 * composed (that IS the reduced-motion state), then — motion permitting —
 * hides the pieces pre-paint and assembles them on viewport entry (kit
 * playOnEnter), starts the ambient layer (bobbing, LED breathe, cursor
 * blink, connector pulses) on entrance completion, pauses ambient
 * off-screen, and adds fine-pointer parallax. No scroll scrub — deliberate
 * (spec: the pages using this own their scroll moments elsewhere).
 */
export default function IsoSceneStage({
  viewBox,
  objects,
  coreId,
  connectors,
  motion,
  shadows,
  connectorStroke = 'rgba(16,16,28,0.4)',
  nodeFill = '#fff',
  nodeStroke = 'rgba(16,16,28,0.5)',
  surfaceRef,
  className,
}: IsoSceneStageProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useGSAP(
    () => {
      const svg = svgRef.current;
      if (!svg) return;
      const q = gsap.utils.selector(svg);
      const mm = gsap.matchMedia();

      // A scene config missing a motion entry would otherwise throw a
      // render-time TypeError the types can't catch (IsoMotion is an open
      // Record) — fall back to a neutral entry and say so in dev.
      const motionOf = (id: string) => {
        const m = motion[id];
        if (!m && process.env.NODE_ENV !== 'production')
          console.warn(`IsoSceneStage: no motion entry for object "${id}" — using {depth:1, rise:0}`);
        return m ?? { depth: 1, rise: 0 };
      };

      const satellites = objects.filter((o) => o.id !== coreId && motionOf(o.id).rise >= 0);
      const dropIns = objects.filter((o) => o.id !== coreId && motionOf(o.id).rise < 0);
      const enterOf = (id: string) => q(`[data-iso-scroll="${id}"] [data-iso-enter]`);

      // ---- Entrance + ambient (skipped for reduced motion: the SSR DOM
      //      already is the composed scene). -------------------------------
      mm.add(NOT_REDUCED, (ctx) => {
        objects.forEach((o) => {
          gsap.set(enterOf(o.id), { y: motionOf(o.id).rise, autoAlpha: 0 });
        });
        gsap.set(q('[data-iso="shadows"]'), { autoAlpha: 0 });
        gsap.set(q('.iso-conn-mask'), { drawSVG: '0%' });
        gsap.set(q('.iso-node'), { autoAlpha: 0 });
        gsap.set(q('[data-iso-label]'), { autoAlpha: 0 });

        const ambient: gsap.core.Tween[] = [];
        let visible = true;

        const startAmbient = () => {
          [...satellites, ...dropIns].forEach((o, i) => {
            ambient.push(
              gsap.to(enterOf(o.id), {
                y: '+=7',
                duration: gsap.utils.random(2.6, 4),
                yoyo: true,
                repeat: -1,
                ease: 'sine.inOut',
                delay: i * 0.35,
              }),
            );
          });
          ambient.push(
            gsap.to(q('[data-part="led"]'), {
              opacity: 0.45, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut',
            }),
            gsap.to(q('[data-part="cursor"]'), {
              opacity: 0, duration: 0.7, yoyo: true, repeat: -1, ease: 'steps(1)',
            }),
          );
          connectors.filter((c) => c.pulse).forEach((c, i) => {
            const pulse = q(`[data-pulse="${c.id}"]`);
            const duration = gsap.utils.random(2.2, 3.2);
            ambient.push(
              gsap.to(pulse, {
                motionPath: { path: `#${c.id}`, align: `#${c.id}`, alignOrigin: [0.5, 0.5] },
                duration, repeat: -1, repeatDelay: 0.6, ease: 'none', delay: i * 0.7,
              }),
              // Glow mid-path, dim at both ends (hides the tuck under the core).
              gsap.fromTo(pulse, { opacity: 0 }, {
                opacity: 1, duration: duration / 2, yoyo: true, repeat: -1,
                repeatDelay: 0.3, ease: 'sine.inOut', delay: i * 0.7,
              }),
            );
          });
          if (!visible) ambient.forEach((t) => t.pause());
        };

        const tl = gsap.timeline({
          paused: true,
          defaults: { ease: 'power3.out' },
          // ctx.add records the ambient tweens into THIS matchMedia context
          // even though they're created async (post-entrance) — so a
          // reduced-motion flip mid-session reverts them to the composed
          // scene instead of freezing a pulse dot mid-path (#224).
          onComplete: () => ctx.add(startAmbient),
        });
        tl.to(enterOf(coreId), { y: 0, autoAlpha: 1, duration: 1.1, ease: 'expo.out' }, 0)
          .to(q('[data-iso="shadows"]'), { autoAlpha: 1, duration: 1.3, ease: 'power2.inOut' }, 0.25)
          .to(satellites.map((o) => enterOf(o.id)[0]), { y: 0, autoAlpha: 1, duration: 0.9, stagger: 0.12 }, '-=0.65');
        dropIns.forEach((o, i) => {
          tl.to(enterOf(o.id), { y: 0, autoAlpha: 1, duration: 0.8 }, i === 0 ? '-=0.55' : '-=0.6');
        });
        tl.to(q('.iso-conn-mask'), { drawSVG: '100%', duration: 0.55, ease: 'power2.inOut', stagger: 0.07 }, '-=0.45')
          .to(q('.iso-node'), { autoAlpha: 1, duration: 0.3, stagger: 0.05 }, '-=0.4')
          .to(q('[data-iso-label]'), { autoAlpha: 1, duration: 0.5, stagger: 0.07 }, '-=0.25');

        const disposeEnter = playOnEnter(tl, svg);

        // Ambient loops pause while the scene is off-screen (playbook rule).
        const io = new IntersectionObserver(
          (entries) => {
            visible = entries.some((e) => e.isIntersecting);
            ambient.forEach((t) => (visible ? t.play() : t.pause()));
          },
          { threshold: 0.1 },
        );
        io.observe(svg);

        return () => {
          disposeEnter?.();
          io.disconnect();
          // revert (not just kill): clears the ambient tweens' inline
          // leftovers so the context revert lands on the composed scene.
          ambient.forEach((t) => t.revert());
        };
      });

      // ---- Pointer parallax (fine pointers only). ------------------------
      mm.add(`(hover: hover) and (pointer: fine) and ${NOT_REDUCED}`, () => {
        const surface = surfaceRef?.current ?? svg.parentElement ?? svg;
        const movers = q('[data-iso-parallax]').map((el) => ({
          xTo: gsap.quickTo(el, 'x', { duration: 0.7, ease: 'power3' }),
          yTo: gsap.quickTo(el, 'y', { duration: 0.7, ease: 'power3' }),
          depth: parseFloat((el as unknown as SVGGElement).dataset.depth ?? '1'),
        }));
        const onMove = (event: Event) => {
          const e = event as PointerEvent;
          const rect = surface.getBoundingClientRect();
          const nx = (e.clientX - rect.left) / rect.width - 0.5;
          const ny = (e.clientY - rect.top) / rect.height - 0.5;
          movers.forEach((m) => {
            m.xTo(nx * 15 * m.depth);
            m.yTo(ny * 10 * m.depth);
          });
        };
        const onLeave = () => movers.forEach((m) => (m.xTo(0), m.yTo(0)));
        surface.addEventListener('pointermove', onMove);
        surface.addEventListener('pointerleave', onLeave);
        return () => {
          surface.removeEventListener('pointermove', onMove);
          surface.removeEventListener('pointerleave', onLeave);
        };
      });
    },
    { scope: svgRef },
  );

  return (
    <svg
      ref={svgRef}
      viewBox={viewBox}
      className={className}
      aria-hidden="true"
      focusable="false"
      style={{ width: '100%', height: 'auto' }}
    >
      <defs>
        <linearGradient id="iso-edge-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--color-brand-600)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--color-brand-500)" stopOpacity="0.15" />
        </linearGradient>
        {/* DrawSVG can't draw dashed strokes directly (it owns stroke-dasharray),
            so each dashed connector is revealed through a mask whose solid
            stroke is what actually gets drawn. */}
        {connectors.map((c) => (
          <mask key={c.id} id={`${c.id}-mask`} maskUnits="userSpaceOnUse">
            <path className="iso-conn-mask" d={c.d} fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
          </mask>
        ))}
      </defs>

      <g data-iso="shadows">
        {shadows.map((s, i) => (
          <Shadow key={i} box={s.box} rx={s.rx} />
        ))}
      </g>

      <g data-iso="connectors">
        {connectors.map((c) => (
          <g key={c.id}>
            <path
              id={c.id}
              d={c.d}
              fill="none"
              stroke={connectorStroke}
              strokeWidth="1.25"
              strokeDasharray="4 5"
              mask={`url(#${c.id}-mask)`}
              style={{ vectorEffect: 'non-scaling-stroke' }}
            />
            <circle className="iso-node" cx={c.node.x} cy={c.node.y} r="3" fill={nodeFill} stroke={nodeStroke} strokeWidth="1.25" />
          </g>
        ))}
      </g>

      {objects.map((o) => (
        <g key={o.id} data-iso-scroll={o.id}>
          <g data-iso-parallax="" data-depth={(motion[o.id] ?? { depth: 1 }).depth}>
            <g data-iso-enter="">
              {o.element}
              {o.label && <IsoLabel x={o.label.x} y={o.label.y} text={o.label.text} lead={o.label.lead} />}
            </g>
          </g>
        </g>
      ))}

      <g data-iso="pulses">
        {connectors.filter((c) => c.pulse).map((c) => (
          <circle key={c.id} data-pulse={c.id} r="4.5" fill="var(--color-brand-500)" opacity="0" cx="-20" cy="-20" />
        ))}
      </g>
    </svg>
  );
}
